// Pure selectors over Renteon booking services.
// Shared by useBookingStore (live booking) and views of a saved booking, so both compute the same thing.

type TServices = IBookingServiceModel[] | null | undefined

export const hasServiceCode = (service: Pick<IBookingServiceModel, 'serviceCode'> | null | undefined, ...codes: ERenteonServiceCode[]) =>
	!!service?.serviceCode && (codes as string[]).includes(service.serviceCode)

export const findServiceByCode = (services: TServices, code: ERenteonServiceCode) => services?.find((service) => service.serviceCode === code)

export const isInsuranceService = (service: IBookingServiceModel) => service.serviceGroupID === EReservationServiceGroupID.INSURANCE

const getServiceMeta = (serviceCode: string | null | undefined) => RENTEON_SERVICE_META[serviceCode as ERenteonServiceCode]

export const isAbroadZoneService = (service: Pick<IBookingServiceModel, 'serviceCode'>) => !!getServiceMeta(service.serviceCode)?.isAbroadZone

export const getServiceIcon = (serviceCode: string | null | undefined) => getServiceMeta(serviceCode)?.icon ?? DEFAULT_SERVICE_ICON

export const pickInsuranceServices = (services: TServices) => services?.filter(isInsuranceService)

export const pickAbroadZoneServices = (services: TServices) => services?.filter(isAbroadZoneService) ?? []

export const pickAddonServices = (services: TServices) =>
	(services ?? [])
		.filter(
			(service) =>
				(service.serviceTypeID === EReservationServiceTypeID.ADDITIONAL_EQUIPMENT ||
					(service.serviceGroupID === EReservationServiceGroupID.PAYMENTS && service.isMandatory === false)) &&
				!hasServiceCode(service, ...ADDON_EXCLUDED_SERVICE_CODES),
		)
		.map((service) => ({ ...service, quantity: !service.isSelected ? 0 : service.quantity }))

export const findSelectedInsurance = (services: TServices) => services?.find((service) => isInsuranceService(service) && service.isSelected)

// selected services other than the car rent itself (insurance, addons, fees)
export const pickSelectedExtraServices = (services: TServices) =>
	services?.filter((service) => service.isSelected && service.serviceGroupID !== EReservationServiceGroupID.CAR_RENT) ?? []

export const getSelectedServiceGroupIDs = (services: TServices) => [
	...new Set(services?.filter((service) => service.isSelected).map((service) => service.serviceGroupID)),
]

const combineServices = (base: IBookingServiceModel, extra: IBookingServiceModel, sumQuantity: boolean): IBookingServiceModel => ({
	...base,
	quantity: sumQuantity ? base.quantity + extra.quantity : base.quantity,
	amountTotal: (base.amountTotal ?? 0) + (extra.amountTotal ?? 0),
})

const removeServiceByCode = (services: IBookingServiceModel[], code: ERenteonServiceCode) => {
	const index = services.findIndex((service) => hasServiceCode(service, code))
	if (index !== -1) services.splice(index, 1)
}

// Shows ADF (additional driver) and ADD (driver fee) as one ADD position. Mutates the lists.
// adfList and addList may be the same array.
export const mergeAdditionalDriverServices = (adfList: IBookingServiceModel[], addList: IBookingServiceModel[]) => {
	const adf = findServiceByCode(adfList, ERenteonServiceCode.ADDITIONAL_DRIVER)
	const add = findServiceByCode(addList, ERenteonServiceCode.ADDITIONAL_DRIVER_FEE)
	if (!add?.amountTotal || !adf?.amountTotal) return

	removeServiceByCode(adfList, ERenteonServiceCode.ADDITIONAL_DRIVER)
	removeServiceByCode(addList, ERenteonServiceCode.ADDITIONAL_DRIVER_FEE)
	addList.push(combineServices(add, adf, true))
}

// Adds the extra location fee (LDD/LDC) to the delivery/collection service (VEH-DEL/VEH-COL). Mutates the list.
export const mergeLocationFeeService = (services: IBookingServiceModel[], baseCode: ERenteonServiceCode, feeCode: ERenteonServiceCode) => {
	const base = findServiceByCode(services, baseCode)
	const fee = findServiceByCode(services, feeCode)
	if (base?.amountTotal == null || fee?.amountTotal == null) return

	removeServiceByCode(services, baseCode)
	removeServiceByCode(services, feeCode)
	services.push(combineServices(base, fee, false))
}

export const getCarRentAmount = (services: TServices) =>
	services?.find((service) => service.serviceTypeID === EReservationServiceTypeID.CAR_RENT)?.amountTotal

export const getAdditionalServicesAmount = (services: TServices) =>
	services
		?.filter(
			(service) =>
				service.isSelected &&
				service.serviceGroupID !== EReservationServiceGroupID.CAR_RENT &&
				service.serviceGroupID !== EReservationServiceGroupID.INSURANCE,
		)
		.reduce((sum, service) => sum + (service.amountTotal ?? 0), 0) ?? 0
